import { createFileRoute, redirect, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { forgetEndedSession, rememberEndedSession } from "@/features/auth/safeFrom";
import { sessionActions, useSession } from "@/features/auth/session";
import { NotFound } from "@/features/public/NotFound";
import { AppShell } from "@/features/shell/AppShell";

function GuardedShell() {
  const session = useSession();
  const navigate = useNavigate();
  const href = useRouterState({ select: (s) => s.location.href });
  // Whose session this was: a return link is only for that user.
  const lastUser = useRef<string | null>(null);
  if (session.status === "authenticated" && session.userId) lastUser.current = session.userId;
  useEffect(() => {
    if (session.status !== "anonymous") return;
    // A deliberate sign-out goes to a clean sign-in page: no "session ended"
    // notice and no return path (nor a record that would block the next
    // user's own links).
    if (sessionActions.wasSignedOutVoluntarily()) {
      forgetEndedSession();
      void navigate({ to: "/login", replace: true });
      return;
    }
    rememberEndedSession(lastUser.current);
    void navigate({ to: "/login", search: { from: href, ended: 1 } });
  }, [session.status, navigate, href]);
  if (session.status !== "authenticated") return null;
  // Keyed by user: when another tab signs in as someone else and this tab
  // adopts that session, nothing typed for the previous user (passwords,
  // drafts) stays on screen or can be submitted as the new one. A same-user
  // refresh or re-login keeps the shell and its drafts.
  return <AppShell key={session.userId ?? ""} />;
}

export const Route = createFileRoute("/app")({
  beforeLoad: async ({ location }) => {
    const s = await sessionActions.whenSettled();
    if (s.status !== "authenticated") {
      // A session that failed to restore leaves this link for its own user
      // only (a fresh anonymous visit has no owner: anyone may follow it).
      if (!sessionActions.wasSignedOutVoluntarily()) rememberEndedSession(sessionActions.lastSessionUser());
      throw redirect({ to: "/login", search: { from: location.href } });
    }
  },
  component: GuardedShell,
  notFoundComponent: () => <NotFound inShell />,
});
